import { useEECEvents } from 'analytics/hooks/useEECEvents/useEECEvents'
import {
	useDispatchFavorites,
	useFavorites,
} from 'favorite/context/FavoritesProvider'
import type { FavoriteProduct, UseFavoriteReturn } from 'favorite/types/types'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useSession } from 'session/src/hooks/useSession'

import { addProduct } from './utils/addProduct'
import { isFavoriteCheck } from './utils/isFavoriteCheck'
import { removeProduct } from './utils/removeProduct'

interface UseFavoriteProps {
	location: string
}

export const useFavorite = ({
	location,
}: UseFavoriteProps): UseFavoriteReturn => {
	const favoriteContext = useFavorites()
	const { sendAddToWishlist, sendRemoveFromWishlist } = useEECEvents()
	const favoritesDispatch = useDispatchFavorites()
	const {
		country: { countryISO, languageISO },
	} = useMasterData()
	const { logged, token, isReady } = useSession()

	const favoritesPageEnabled = favoriteContext?.favoritesPageEnabled

	const get = (product: FavoriteProduct) => {
		const favorites = favoriteContext?.favorites

		if (!favorites) {
			return false
		}
		return isFavoriteCheck(product, favorites)
	}

	const add = async (product: FavoriteProduct): Promise<void> => {
		sendAddToWishlist({
			item: {
				colorId: product.color,
				productId: product.ref,
				quantity: 1,
			},
			location,
		})
		return addProduct({
			logged,
			token,
			favoritesPageEnabled,
			product,
			countryISO,
			languageISO,
			favoritesDispatch,
		})
	}

	const remove = (product: FavoriteProduct) => {
		sendRemoveFromWishlist({
			item: {
				colorId: product.color,
				productId: product.ref,
				quantity: 1,
			},
			location,
		})
		return removeProduct({
			favoritesPageEnabled,
			product,
			favoritesDispatch,
			logged,
			token,
		})
	}

	const isLoading =
		typeof favoriteContext?.isLoading === 'boolean'
			? favoriteContext?.isLoading
			: isReady

	return {
		get,
		add,
		remove,
		isLoading,
	}
}
